import type { ParseKeys, TFunction } from 'i18next'
import {
  Banknote,
  Briefcase,
  Check,
  ClipboardList,
  FileText,
  HandCoins,
  History,
  MessageSquare,
  Package,
  Pencil,
  RotateCcw,
  Route,
  Send,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { EmptyState } from '@/components/feedback/EmptyState'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { Card, CardBody } from '@/components/ui/Card'
import { useFormat } from '@/i18n/useFormat'
import { cn } from '@/lib/cn'
import { useQuoteHistory } from '@/features/quotes/api/useQuote'
import type { QuoteActionEntry } from '@/types/domain'

/* The quote's History tab, as a component rather than as page markup.
 *
 * It lived inside QuoteDetailPage — the section, the label catalogue and the
 * wording helper together — while every one of its neighbours had already moved
 * to QuoteSections. That asymmetry was the only reason the page still imported
 * a query hook, a timestamp formatter and four Card parts it otherwise had no
 * use for. Nothing about this section knows which surface is hosting it; the
 * page keeps the decision about WHEN to mount it, which is the whole of what a
 * page owes a tab.
 */

/* The action log's machine keys, mapped to the catalogue.
 *
 * The server stores "quote.cargo.create", never a sentence: the log is written
 * once and read in two languages, so the words have to be chosen at render time
 * by the layer that knows who is reading. This map is that choice.
 *
 * A key absent here is NOT an error to swallow. A new endpoint starts writing
 * its own action the day it ships, and this page is not redeployed in the same
 * breath — so an unmapped entry renders as its raw key: ugly, but present and
 * searchable, which is what an audit trail owes its reader. */
const ACTION_LABELS: Record<string, ParseKeys> = {
  'quote.create': 'quotes.history.actions.created',
  'quote.update': 'quotes.history.actions.updated',
  'quote.cargo.create': 'quotes.history.actions.cargoAdded',
  'quote.cargo.update': 'quotes.history.actions.cargoUpdated',
  'quote.cargo.delete': 'quotes.history.actions.cargoRemoved',
  'quote.cost-line.create': 'quotes.history.actions.costLineAdded',
  'quote.cost-line.update': 'quotes.history.actions.costLineUpdated',
  'quote.cost-line.delete': 'quotes.history.actions.costLineRemoved',
  'quote.cost-line.batch': 'quotes.history.actions.costLinesBatchAdded',
  'quote.cost-line.markup': 'quotes.history.actions.markupApplied',
  'quote.route-option.create': 'quotes.history.actions.routeOptionAdded',
  'quote.route-option.update': 'quotes.history.actions.routeOptionUpdated',
  'quote.route-option.select': 'quotes.history.actions.routeOptionSelected',
  'quote.route-option.delete': 'quotes.history.actions.routeOptionRemoved',
  'quote.block.create': 'quotes.history.actions.blockAdded',
  'quote.block.update': 'quotes.history.actions.blockUpdated',
  'quote.block.delete': 'quotes.history.actions.blockRemoved',
  'quote.send': 'quotes.history.actions.sent',
  'quote.reopen': 'quotes.history.actions.reopened',
  'quote.convert': 'quotes.history.actions.converted',
  // The pricing state's acts (S-2a, cmd/asa/actions.go).
  'quote.pricing.request': 'quotes.history.actions.pricingRequested',
  'quote.pricing.ready': 'quotes.history.actions.pricingReady',
  'quote.pricing.withdraw': 'quotes.history.actions.pricingWithdrawn',
  // The quote's chat, its parties and its team (cmd/asa/actions.go).
  'quote.message.post': 'quotes.history.actions.messagePosted',
  'quote.participant.add': 'quotes.history.actions.participantAdded',
  'quote.participant.update': 'quotes.history.actions.participantUpdated',
  'quote.participant.remove': 'quotes.history.actions.participantRemoved',
  'quote.team.add': 'quotes.history.actions.teamAdded',
  'quote.team.remove': 'quotes.history.actions.teamRemoved',
}

/* The client's answer is one action carrying WHICH answer in its annotation,
 * because that is how the server writes it. "Client answered" on both would
 * lose the only part anybody rereads the log for.
 *
 * "approved" IS STILL READ HERE even though the endpoint no longer accepts it
 * (migration 0050 made accepting an offer conversion's act). The action log is
 * a record of what happened, not of what is currently possible: entries written
 * before the change say `approved`, and dropping the branch would re-label
 * years of real history as the generic "Client answer recorded" — losing
 * exactly the fact the annotation exists to keep. New acceptances arrive as
 * `quote.convert` instead. */
function actionText(entry: QuoteActionEntry, t: TFunction): string {
  if (entry.action === 'quote.client-answer') {
    const answer = entry.detail?.answer
    if (answer === 'approved') return t('quotes.history.actions.answerApproved')
    if (answer === 'rejected') return t('quotes.history.actions.answerRejected')
    return t('quotes.history.actions.answerRecorded')
  }

  const key = ACTION_LABELS[entry.action]
  return key ? t(key) : entry.action
}

/* ── The rail's badges ────────────────────────────────────────────────────── */

/* The tint is a TOKEN PAIR, never a raw Tailwind colour.
 *
 * `bg-brand-soft`/`text-brand` and the semantic trio beside it are declared once
 * in styles/tokens.css under Tailwind's `@theme`, which is what makes every one
 * of them simultaneously a utility class and a CSS variable. A badge painted
 * `bg-blue-50 text-blue-600` instead would look identical today and would be the
 * one surface a palette swap could not reach — the app carries no `dark:`
 * variant and no `data-theme` attribute anywhere, so a second theme arrives here
 * as new values for these variables and nothing else. Each pair is a tint and
 * its own ink, so the icon and the disc it sits on can never drift apart.
 *
 * And the trio is SEMANTIC, not decorative. The reference design varies its
 * badge hues for rhythm; this system spends positive/negative on meaning only,
 * so an amber "cargo position added" would be a warning about a routine edit.
 * The variety here therefore runs along the one axis that is true: the quote's
 * own lifecycle is branded, the client's verdict is coloured by the verdict, and
 * the ordinary editing that fills most of a log is quiet. */
type BadgeTone = 'brand' | 'positive' | 'negative' | 'neutral'

const TONE_CLASS: Record<BadgeTone, string> = {
  brand: 'bg-brand-soft text-brand',
  positive: 'bg-positive-soft text-positive',
  negative: 'bg-negative-soft text-negative',
  neutral: 'bg-neutral-100 text-text-muted',
}

interface ActionBadge {
  icon: LucideIcon
  tone: BadgeTone
}

/* Icons group BY CONCEPT rather than one per verb, exactly as the case feed's
 * families do (see CaseHistory): three glyphs for added/updated/removed cargo
 * would be three things to learn and nothing to read, where one parcel down the
 * rail is something a reader can skim for. What the log calls a create and a
 * delete, the rail calls cargo — the sentence beside it already says which.
 *
 * `quote.client-answer` is deliberately ABSENT: its badge is chosen from the
 * annotation, below, for the same reason its wording is. */
const ACTION_BADGES: Record<string, ActionBadge> = {
  'quote.create': { icon: ClipboardList, tone: 'brand' },
  'quote.update': { icon: Pencil, tone: 'neutral' },
  'quote.cargo.create': { icon: Package, tone: 'neutral' },
  'quote.cargo.update': { icon: Package, tone: 'neutral' },
  'quote.cargo.delete': { icon: Package, tone: 'neutral' },
  'quote.cost-line.create': { icon: Banknote, tone: 'neutral' },
  'quote.cost-line.update': { icon: Banknote, tone: 'neutral' },
  'quote.cost-line.delete': { icon: Banknote, tone: 'neutral' },
  'quote.cost-line.batch': { icon: Banknote, tone: 'neutral' },
  'quote.cost-line.markup': { icon: Banknote, tone: 'neutral' },
  'quote.route-option.create': { icon: Route, tone: 'neutral' },
  'quote.route-option.update': { icon: Route, tone: 'neutral' },
  'quote.route-option.select': { icon: Route, tone: 'neutral' },
  'quote.route-option.delete': { icon: Route, tone: 'neutral' },
  'quote.block.create': { icon: FileText, tone: 'neutral' },
  'quote.block.update': { icon: FileText, tone: 'neutral' },
  'quote.block.delete': { icon: FileText, tone: 'neutral' },
  /* The four milestones of the quote's own life, branded because they are the
     entries somebody scrolls this list to find. Briefcase is the case glyph the
     global search already uses, so "converted" points at the thing it produced
     rather than at a generic arrow. */
  'quote.send': { icon: Send, tone: 'brand' },
  'quote.reopen': { icon: RotateCcw, tone: 'brand' },
  'quote.convert': { icon: Briefcase, tone: 'brand' },
  /* The price request's life (S-2a), one glyph — the «Расчёт» card's own —
     with the tone saying where it went: asked is work in progress (the
     brand's one other meaning), ready is the success tone, withdrawn is
     quiet. The same three tones the pricing pill wears. */
  'quote.pricing.request': { icon: HandCoins, tone: 'brand' },
  'quote.pricing.ready': { icon: HandCoins, tone: 'positive' },
  'quote.pricing.withdraw': { icon: HandCoins, tone: 'neutral' },
  /* Ordinary collaboration, so quiet. The parties and the team share the
     people glyph, as the case feed's `people` family does; a message is the
     chat's own glyph. */
  'quote.message.post': { icon: MessageSquare, tone: 'neutral' },
  'quote.participant.add': { icon: Users, tone: 'neutral' },
  'quote.participant.update': { icon: Users, tone: 'neutral' },
  'quote.participant.remove': { icon: Users, tone: 'neutral' },
  'quote.team.add': { icon: Users, tone: 'neutral' },
  'quote.team.remove': { icon: Users, tone: 'neutral' },
}

/* THE FALLBACK IS NOT OPTIONAL, and it is the same argument ACTION_LABELS makes
 * about an unmapped key rendering as itself: `backend/cmd/asa/actions.go` is a
 * live file, a route mounted next month starts writing its own action the day it
 * ships, and this page is not redeployed in the same breath. That day must
 * produce a filled badge beside a slightly ugly sentence — never a hollow ring,
 * which reads as a rendering bug and invites somebody to "fix" a real entry by
 * hiding it.
 *
 * The section's OWN icon, deliberately: the empty state already uses it to mean
 * "this is the record of what happened", which is the most an entry this build
 * has never heard of can honestly claim. A neutral tint, because a tone would be
 * a guess about a meaning nobody here knows. */
const FALLBACK_BADGE: ActionBadge = { icon: History, tone: 'neutral' }

function actionBadge(entry: QuoteActionEntry): ActionBadge {
  /* Branching on the annotation exactly where `actionText` does. The verdict is
     the only part of a client answer worth a colour, and it is the only part the
     entry itself carries — a single "client answered" glyph would throw away the
     one fact the reader came back for. */
  if (entry.action === 'quote.client-answer') {
    const answer = entry.detail?.answer
    if (answer === 'approved') return { icon: Check, tone: 'positive' }
    if (answer === 'rejected') return { icon: X, tone: 'negative' }
    return { icon: MessageSquare, tone: 'neutral' }
  }

  return ACTION_BADGES[entry.action] ?? FALLBACK_BADGE
}

/* ── The section ──────────────────────────────────────────────────────────── */

/** One entry on the rail: a tinted disc, the connector below it, and the act. */
function HistoryRow({ entry, last }: { entry: QuoteActionEntry; last: boolean }) {
  const { t } = useTranslation()
  const format = useFormat()
  const { icon: Icon, tone } = actionBadge(entry)

  return (
    /* `group` so the last row can drop its bottom padding: the rail's spacing is
       the gap BETWEEN entries, and a trailing one would leave the card looking
       bottom-heavy under an odd number of lines. */
    <li className="group flex gap-3">
      <div className="flex flex-col items-center">
        <span
          aria-hidden
          data-history-badge
          className={cn(
            'mt-px flex size-7 shrink-0 items-center justify-center rounded-full',
            TONE_CLASS[tone],
          )}
        >
          <Icon size={13} />
        </span>
        {/* The connector runs BETWEEN discs, so it is a child of the row above
            the gap and is simply not rendered on the last one — a single line
            drawn behind the whole list would dangle past the final badge into
            the card's padding, which reads as a list that was cut off. `flex-1`
            rather than a height: the row it belongs to is as tall as its text,
            and the line has to reach whatever that turns out to be. */}
        {last ? null : (
          <span aria-hidden data-history-connector className="bg-line w-px flex-1" />
        )}
      </div>

      <div className="group-last:pb-0 flex min-w-0 flex-1 flex-col gap-0.5 pb-4">
        {/* Title and timestamp on one row, the time pushed to the far edge: a
            column of right-aligned times is a scale the eye reads down, where
            times trailing each title at their own indent are not.

            The reference design sets the title bold. Here it is the heading
            COLOUR at regular weight against light-weight detail beneath, because
            weight 600 in this system belongs to four sanctioned roles — KPI
            values, record identifiers, computed totals, status pill labels — and
            an action log is none of them. The contrast is the same; the ramp
            stays honest. */}
        <div className="flex items-baseline justify-between gap-3">
          {/* `min-w-0` beside the truncate, and it is not decoration: a flex
              item's default minimum width is its content width, so a nowrap
              title would refuse to shrink and push the timestamp off the row
              instead of ellipsising — which is exactly the case a long Russian
              label produces. */}
          <span className="text-text-heading min-w-0 truncate text-[13px]">
            {actionText(entry, t)}
          </span>
          {/* `shrink-0` and `tabular-nums` through asa-num: the times are the one
              column here that must line up, and a long translated title must
              squeeze itself rather than the timestamp. */}
          <span className="asa-num text-text-muted shrink-0 text-[12px] font-light">
            {format.timestamp(entry.occurredAt)}
          </span>
        </div>

        {/* The actor, on its own line and as its own text node. The two facts
            used to share one span joined by a middle dot, which made "who" and
            "when" a single unsearchable string — and put the time in the middle
            of the row instead of at its edge. */}
        <span className="text-text-muted text-[12px] leading-snug font-light">
          {entry.actorName}
        </span>

        {/* An edit that returned a ready price to «Ждёт расчёта» (S-2a). The
            server annotates the causing entry rather than writing a reset of
            its own, so the fact reads under the edit that did it. */}
        {entry.detail?.pricing === 'reopened' ? (
          <span className="text-text-muted text-[12px] leading-snug font-light">
            {t('quotes.history.pricingReopened')}
          </span>
        ) : null}
      </div>
    </li>
  )
}

/* The history (FR-7): the global action log narrowed to this quote. It opens
 * in a side sheet from the quote header's «⋯» menu (S-3); it was a tab. The
 * sheet carries the title, so the card carries none (F-5).
 *
 * Fetched only while that sheet is open (`enabled`) — the log is a separate
 * read, and a page that opens on the cargo should not pay for a list nobody
 * asked to see. */
export function QuoteHistorySection({
  quoteId,
  enabled,
}: {
  quoteId: string
  enabled: boolean
}) {
  const { t } = useTranslation()
  const { data, isPending, isError, error, refetch } = useQuoteHistory(quoteId, {
    enabled,
  })
  const entries = data ?? []

  return (
    <Card>
      {isError ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : isPending ? (
        <CardBody>
          <Skeleton className="h-24 w-full rounded-md" />
        </CardBody>
      ) : entries.length === 0 ? (
        <EmptyState
          icon={History}
          title={t('quotes.history.emptyTitle')}
          description={t('quotes.history.emptyDescription')}
        />
      ) : (
        <ul className="flex flex-col px-[18px] py-4">
          {entries.map((entry, index) => (
            <HistoryRow
              key={entry.id}
              entry={entry}
              last={index === entries.length - 1}
            />
          ))}
        </ul>
      )}
    </Card>
  )
}
